import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Wallet, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from '@/i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER = {
  silver: { label: 'Silver', surface: '#141414', edge: 'rgba(255,255,255,0.14)', ink: '#D8D8D8' },
  gold: { label: 'Gold', surface: '#101010', edge: 'rgba(255,255,255,0.22)', ink: '#F2F2F2' },
  vip: { label: 'VIP', surface: '#070707', edge: 'rgba(255,255,255,0.30)', ink: '#FFFFFF' },
  corporate: { label: 'Corporate', surface: '#0C0C0C', edge: 'rgba(255,255,255,0.18)', ink: '#E4E4E4' },
};

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/.test(navigator.userAgent);

const Guilloche = ({ opacity = 0.5 }) => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 252" preserveAspectRatio="none" aria-hidden="true" style={{ opacity }}>
    <defs>
      <linearGradient id="vc-line" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.16" />
        <stop offset="55%" stopColor="#ffffff" stopOpacity="0.04" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0.12" />
      </linearGradient>
    </defs>
    {Array.from({ length: 26 }).map((_, i) => (
      <ellipse
        key={i}
        cx={300 - i * 3}
        cy={126}
        rx={150 - i * 4}
        ry={118 - i * 3.6}
        fill="none"
        stroke="url(#vc-line)"
        strokeWidth="0.45"
        transform={`rotate(${i * 2.2} 300 126)`}
      />
    ))}
  </svg>
);

export const MemberCard = ({ user }) => {
  const [flipped, setFlipped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [sheen, setSheen] = useState({ x: 50, y: 30 });
  const [loadingWallet, setLoadingWallet] = useState(false);
  const ref = useRef(null);
  const { t } = useTranslation();

  const tier = (user.membership_tier || 'silver').toLowerCase();
  const th = TIER[tier] || TIER.silver;
  const showBack = flipped || hovered;

  const since = user.member_since
    ? new Date(user.member_since).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase()
    : '—';

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setSheen({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const addToGoogleWallet = async () => {
    setLoadingWallet(true);
    try {
      const { data } = await axios.get(`${API}/wallet/google`, { withCredentials: true });
      if (data.save_link) {
        window.open(data.save_link, '_blank');
        toast.success(t('vc.card.wallet-opening'));
      }
    } catch {
      toast.error(t('vc.card.wallet-fail'));
    } finally {
      setLoadingWallet(false);
    }
  };

  const saveAsImage = () => {
    const el = document.querySelector('[data-testid="member-card-front"]');
    if (!el) return;
    import('html2canvas')
      .then(({ default: html2canvas }) => html2canvas(el, { backgroundColor: '#050505', scale: 2 }))
      .then((canvas) => {
        const link = document.createElement('a');
        link.download = `vangardclub-card-${user.member_id}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success(t('vc.card.saved'));
      })
      .catch(() => toast.error(t('vc.card.save-fail')));
  };

  return (
    <div className="w-full max-w-lg">
      <div
        ref={ref}
        className="card-flip-container w-full cursor-pointer select-none"
        style={{ aspectRatio: '1.586 / 1' }}
        onClick={() => setFlipped((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setSheen({ x: 50, y: 30 }); }}
        onMouseMove={onMove}
        data-testid="member-card"
      >
        <div className={`card-flip-inner ${showBack ? 'flipped' : ''}`}>
          {/* ---------- FRONT ---------- */}
          <div
            className="card-flip-front overflow-hidden rounded-[20px]"
            style={{
              background: th.surface,
              boxShadow: `0 40px 80px -30px rgba(0,0,0,0.95), inset 0 0 0 1px ${th.edge}, inset 0 1px 0 rgba(255,255,255,0.10)`,
            }}
            data-testid="member-card-front"
          >
            {/* engraved guilloche + marble grain + pointer sheen */}
            <Guilloche opacity={0.55} />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundImage: 'url(/marble-dark.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.22, mixBlendMode: 'screen' }}
            />
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-500"
              style={{
                background: `radial-gradient(420px circle at ${sheen.x}% ${sheen.y}%, rgba(255,255,255,0.16), transparent 60%)`,
                opacity: hovered ? 1 : 0.45,
              }}
            />

            <div className="relative h-full p-6 sm:p-7 flex flex-col justify-between">
              {/* inner engraved frame + monogram watermark */}
              <div className="absolute inset-[10px] rounded-[13px] pointer-events-none" style={{ border: `1px solid rgba(255,255,255,0.055)` }} />
              <div
                className="absolute -bottom-6 right-2 font-serif italic pointer-events-none leading-none"
                style={{ color: 'rgba(255,255,255,0.035)', fontSize: '150px' }}
                aria-hidden="true"
              >
                V
              </div>
              {/* top row */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center font-serif text-[15px] italic"
                    style={{ color: th.ink, border: `1px solid ${th.edge}`, background: 'rgba(255,255,255,0.04)' }}
                  >
                    V
                  </span>
                  <div className="leading-none">
                    <div className="font-serif text-[17px] sm:text-[19px]" style={{ color: th.ink }}>
                      Vangard<span className="opacity-55">Club</span>
                    </div>
                    <div className="text-[7px] tracking-[0.42em] uppercase mt-1" style={{ color: `${th.ink}66` }}>
                      Private Business Club
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[8px] tracking-[0.4em] uppercase" style={{ color: `${th.ink}88` }}>{th.label}</div>
                  <div className="mt-1.5 h-[1px] w-14 ml-auto" style={{ background: `linear-gradient(90deg, transparent, ${th.ink}55)` }} />
                </div>
              </div>

              {/* engraved name + id */}
              <div>
                <div
                  className="font-serif text-2xl sm:text-[28px] leading-none"
                  style={{ color: th.ink, textShadow: '0 1px 0 rgba(0,0,0,0.85), 0 -1px 0 rgba(255,255,255,0.10)' }}
                  data-testid="card-member-name"
                >
                  {user.name}
                </div>
                <div className="mt-4 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[7px] tracking-[0.4em] uppercase" style={{ color: `${th.ink}66` }}>Member since</div>
                    <div className="text-[12px] tracking-[0.18em] mt-1" style={{ color: `${th.ink}D0` }}>{since}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[7px] tracking-[0.4em] uppercase" style={{ color: `${th.ink}66` }}>Member №</div>
                    <div
                      className="font-mono text-[17px] sm:text-[19px] tracking-[0.22em] mt-0.5"
                      style={{ color: th.ink, textShadow: '0 1px 0 rgba(0,0,0,0.8)' }}
                      data-testid="card-member-id"
                    >
                      {user.member_id}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute bottom-2.5 left-0 right-0 text-center text-[7px] tracking-[0.4em] uppercase pointer-events-none"
              style={{ color: `${th.ink}40` }}
            >
              {t('vc.card.hint')}
            </div>
          </div>

          {/* ---------- BACK ---------- */}
          <div
            className="card-flip-back overflow-hidden rounded-[20px]"
            style={{
              background: '#0A0A0A',
              boxShadow: `0 40px 80px -30px rgba(0,0,0,0.95), inset 0 0 0 1px ${th.edge}`,
            }}
            data-testid="member-card-back"
          >
            <div className="absolute top-0 left-0 right-0 h-8" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0))' }} />
            <div
              className="absolute top-8 left-0 right-0 h-10"
              style={{
                background: 'repeating-linear-gradient(90deg, #1B1B1B 0px, #232323 1px, #141414 2px, #1B1B1B 3px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.8)',
              }}
            />
            <div className="absolute top-[9px] left-6 text-[8px] tracking-[0.42em] uppercase text-neutral-500">VangardClub</div>
            <Guilloche opacity={0.28} />

            <div className="relative h-full pt-[72px] px-6 pb-5 flex items-center gap-5">
              <div className="absolute inset-[10px] rounded-[13px] pointer-events-none" style={{ border: '1px solid rgba(255,255,255,0.05)' }} />
              <div className="bg-white p-2.5 rounded-xl shadow-[0_18px_40px_-16px_rgba(0,0,0,0.9)] shrink-0">
                <QRCodeSVG value={String(user.member_id)} size={92} level="H" bgColor="#FFFFFF" fgColor="#050505" data-testid="member-qr-code" />
              </div>
              <div className="min-w-0">
                <div className="text-[7px] tracking-[0.4em] uppercase text-neutral-500">Member №</div>
                <div className="font-mono text-xl tracking-[0.2em] text-white mt-1" data-testid="card-back-member-id">{user.member_id}</div>
                <div className="text-[7px] tracking-[0.4em] uppercase text-neutral-500 mt-4">{th.label}</div>
                <p className="text-[11px] text-neutral-400 font-light leading-relaxed mt-3 max-w-[150px]">
                  {t('vc.card.present')}
                </p>
              </div>
            </div>

            <div className="absolute bottom-2.5 left-0 right-0 text-center text-[7px] tracking-[0.4em] uppercase text-neutral-600">
              VANGARDCLUB.COM
            </div>
          </div>
        </div>
      </div>

      {/* actions */}
      <div className="mt-6 flex flex-wrap gap-2.5">
        {(isAndroid() || !isIOS()) && (
          <button
            onClick={addToGoogleWallet}
            disabled={loadingWallet}
            className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase text-neutral-200 hover:bg-white/5 hover:border-white/45 disabled:opacity-40 transition-colors"
            data-testid="add-to-google-wallet-btn"
          >
            {loadingWallet ? <Loader2 size={13} className="animate-spin" /> : <Wallet size={13} />}
            {t('vc.card.google-wallet')}
          </button>
        )}
        {isIOS() && (
          <button
            onClick={() => toast.info(t('vc.card.apple-soon'))}
            className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase text-neutral-200 hover:bg-white/5 transition-colors"
            data-testid="add-to-apple-wallet-btn"
          >
            <Wallet size={13} /> {t('vc.card.apple-wallet')}
          </button>
        )}
        <button
          onClick={saveAsImage}
          className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase text-neutral-200 hover:bg-white/5 hover:border-white/45 transition-colors"
          data-testid="save-card-image-btn"
        >
          <Download size={13} /> {t('vc.card.save-image')}
        </button>
      </div>
    </div>
  );
};

export default MemberCard;
